import { useState, type ReactNode } from 'react'
import { adminMutate } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { messageEchecMutation } from '../../api.js'
import { LIBELLE_TYPE_ANNOTATION } from './modele.js'
import { ecrireSonde } from '../registre/ecrire.js'

/**
 * GESTES ÉCRITS DES FICHES (21/10/2026) : une zone de texte et son bouton.
 * Chaque envoi porte une clé d'idempotence neuve ; `adminMutate` la rejoue
 * À L'IDENTIQUE après une confirmation d'identité. Aucun texte saisi n'est
 * perdu sur un refus : il reste dans la zone.
 */

export const nouvelleCle = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)

/** POST vers le relais avec clé d'idempotence ; rend null si réussi, sinon le message en clair. */
export async function envoyer(chemin: string, corps: unknown, rien = 'rien n’a été enregistré'): Promise<string | null> {
  try {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/supervision/v/${chemin}`, corps, {
      headers: { 'Idempotency-Key': nouvelleCle() },
    })
    if (r.status >= 200 && r.status < 300) return null
    return messageEchecMutation(r.status, r.data, rien)
  } catch {
    return `Gestion n’a pas répondu — ${rien}.`
  }
}

export type Sourdine = '30m' | 'night'

/**
 * SOURDINE D'UN INCIDENT (Sonde 593166c, 24/10/2026) — `POST|DELETE
 * incidents/:id/snooze`, droit `sonde.incident.ack`. Pendant la sourdine,
 * Sonde n'envoie rien pour l'incident, sauf la fin d'une alerte déjà
 * annoncée ; à l'échéance, s'il est toujours ouvert, elle le rappelle.
 * « night » = 8 h, la fin de la nuit commerciale. Rend null si réussi.
 */
export async function sourdineIncident(id: string, jusqua: Sourdine | null): Promise<string | null> {
  const chemin = `incidents/${encodeURIComponent(id)}/snooze`
  const e = jusqua
    ? await ecrireSonde('POST', chemin, { until: jusqua }, { rien: 'aucune sourdine n’a été posée' })
    : await ecrireSonde('DELETE', chemin, undefined, { rien: 'la sourdine court toujours' })
  return e.ok ? null : e.message
}

export const CONFIRMER_NUIT = {
  title: 'Couper cet incident pour la nuit ?',
  message: 'Plus aucune notification pour cet incident jusqu’à 8 h. S’il est toujours ouvert à 8 h, Sonde vous le rappelle.',
  variant: 'standard' as const,
  confirmLabel: 'Couper pour la nuit',
}

export function ZoneTexte({ valeur, onChange, max, placeholder, libelle }: { valeur: string; onChange: (v: string) => void; max: number; placeholder: string; libelle: string }) {
  return (
    <label className="sv-fiche-champ">
      <span className="sv-fiche-champ__libelle">{libelle}</span>
      <textarea className="sv-fiche-zone-texte" value={valeur} maxLength={max} placeholder={placeholder} rows={3}
                onChange={(e) => onChange(e.target.value)} />
      <span className="sv-fiche-champ__compte">{valeur.length}/{max}</span>
    </label>
  )
}

export function RetourGeste({ r }: { r: { ok: boolean; texte: string } | null }) {
  if (!r) return null
  return <p className={`sv-fiche-retour sv-fiche-retour--${r.ok ? 'ok' : 'non'}`} role="status">{r.texte}</p>
}

type TypeAnnotation = 'note' | 'balance' | 'intervention' | 'incident' | 'information'

/**
 * Annotation de la régie (droit `sonde.annotate`) : un fait daté de
 * maintenant, rattaché à un appareil, une salle ou un incident.
 */
export function FormulaireAnnotation({ cible, onFait, titre }: {
  cible: { device_uid?: string; zone_code?: string; incident_id?: string }
  onFait: () => void
  titre?: ReactNode
}) {
  const [type, setType] = useState<TypeAnnotation>('note')
  const [texte, setTexte] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [retour, setRetour] = useState<{ ok: boolean; texte: string } | null>(null)
  const envoyerAnnotation = async () => {
    if (!texte.trim()) return
    setEnCours(true); setRetour(null)
    const err = await envoyer('annotations', { body: texte.trim(), kind: type, ...cible })
    setEnCours(false)
    if (err) { setRetour({ ok: false, texte: err }); return }
    setTexte('')
    setRetour({ ok: true, texte: 'Annotation enregistrée dans Sonde.' })
    onFait()
  }
  return (
    <div className="sv-fiche-form">
      {titre}
      <SegmentedControl size="sm" ariaLabel="Type d’annotation" value={type} onChange={setType}
        options={(['note', 'intervention', 'balance', 'information'] as const).map((k) => ({ value: k, label: LIBELLE_TYPE_ANNOTATION[k] ?? k }))} />
      <ZoneTexte libelle="Ce qui s’est passé" valeur={texte} onChange={setTexte} max={2000} placeholder="Ex. : câble du strobe 6 rebranché par la régie." />
      <div className="sv-fiche-form__actions">
        <button type="button" className="lc-btn lc-btn--primary" disabled={enCours || !texte.trim()} onClick={() => { void envoyerAnnotation() }}>
          {enCours ? 'Enregistrement…' : 'Annoter'}
        </button>
      </div>
      <RetourGeste r={retour} />
    </div>
  )
}
